import React, { createRef, useCallback, useMemo, useRef } from "react";
import { SwitchTransition, CSSTransition } from "react-transition-group";
import { SwitchTransitionProps } from "react-transition-group/SwitchTransition";

// helpers & hooks
import { withStyles } from "@core-ui/helpers/WithStyles";

import styles from "./style.scss";

interface ScreenTransitionProps {
    children: React.ReactElement;
    forward?: boolean;
    transitionMode?: SwitchTransitionProps["mode"];
    /** Fires when the entering route finishes its enter transition. */
    onEntered?: VoidFunction;
    /** Fires when the exiting route finishes its exit transition. */
    onExited?: VoidFunction;
}

function ScreenTransition({
    children,
    forward = true,
    transitionMode = "in-out",
    onEntered,
    onExited,
}: ScreenTransitionProps): React.ReactElement | React.ReactElement[] {
    const key = children?.key ?? "no_key";
    const itemMap = useRef({ [key]: { component: children, nodeRef: createRef<HTMLDivElement>() } });
    itemMap.current[key] = { component: children, nodeRef: createRef() };
    const currentItem = itemMap.current[key];

    const classNames = useMemo(() => {
        return !forward
            ? {
                  enter: styles.containerEnterForward,
                  enterActive: styles.containerEnterForwardActive,
                  enterDone: styles.containerEnterForwardDone,
                  exit: styles.containerExitForward,
                  exitActive: styles.containerExitForwardActive,
                  exitDone: styles.containerExitForwardDone,
              }
            : {
                  enter: styles.containerEnter,
                  enterActive: styles.containerEnterActive,
                  enterDone: styles.containerEnterDone,
                  exit: styles.containerExit,
                  exitActive: styles.containerExitActive,
                  exitDone: styles.containerExitDone,
              };
    }, [forward]);

    const useEndListener = useCallback(
        (done: VoidFunction) => {
            currentItem.nodeRef?.current && currentItem.nodeRef.current.addEventListener("transitionend", done, false);
        },
        [currentItem.nodeRef],
    );

    if (!currentItem) {
        return <></>;
    }

    return (
        <SwitchTransition mode={transitionMode}>
            <CSSTransition
                key={key}
                nodeRef={currentItem.nodeRef}
                classNames={classNames}
                addEndListener={useEndListener}
                onEntered={onEntered}
                onExited={onExited}
            >
                <div ref={currentItem.nodeRef} className={styles.container}>
                    {currentItem.component}
                </div>
            </CSSTransition>
        </SwitchTransition>
    );
}

export default withStyles(ScreenTransition as React.FC<ScreenTransitionProps>, styles);
